import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, Check, Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

interface ClientTagsInputProps {
  tags: string[];
  onAddTag: (tag: string) => Promise<void>;
  onRemoveTag: (tag: string) => Promise<void>;
  canEdit?: boolean;
  showAddButton?: boolean; // Only show Add Tag on hover (like pet page)
  className?: string;
}

interface TagSuggestion {
  name: string;
  color?: string;
}

// Tag color definitions matching pet page behavioral tags style
const TAG_COLORS: { bg: string; text: string; border: string }[] = [
  { bg: 'bg-orange-100 dark:bg-orange-500/20', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-500' },
  { bg: 'bg-blue-100 dark:bg-blue-500/20', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-500' },
  { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-500' },
  { bg: 'bg-amber-100 dark:bg-amber-500/20', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-500' },
  { bg: 'bg-purple-100 dark:bg-purple-500/20', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-500' },
  { bg: 'bg-rose-100 dark:bg-rose-500/20', text: 'text-rose-700 dark:text-rose-300', border: 'border-rose-500' },
  { bg: 'bg-cyan-100 dark:bg-cyan-500/20', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-500' },
  { bg: 'bg-indigo-100 dark:bg-indigo-500/20', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-500' },
];

// Special tag mappings for common tags
const SPECIAL_TAG_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  'vip': { bg: 'bg-amber-100 dark:bg-amber-500/20', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-500' },
  'new client': { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-500' },
  'new': { bg: 'bg-emerald-100 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-500' },
  'referral': { bg: 'bg-purple-100 dark:bg-purple-500/20', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-500' },
  'difficult': { bg: 'bg-rose-100 dark:bg-rose-500/20', text: 'text-rose-700 dark:text-rose-300', border: 'border-rose-500' },
  'caution': { bg: 'bg-orange-100 dark:bg-orange-500/20', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-500' },
  'preferred': { bg: 'bg-blue-100 dark:bg-blue-500/20', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-500' },
  'follow-up': { bg: 'bg-cyan-100 dark:bg-cyan-500/20', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-500' },
};

const ClientTagsInput: React.FC<ClientTagsInputProps> = ({
  tags,
  onAddTag,
  onRemoveTag,
  canEdit = true,
  showAddButton = true,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [savingTag, setSavingTag] = useState<string | null>(null);
  const [savedTag, setSavedTag] = useState<string | null>(null);
  const [removingTag, setRemovingTag] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch tag suggestions when component mounts
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const response = await fetch(API_ENDPOINTS.CLIENT_TAGS.LIST, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.tags || []);
        }
      } catch (error) {
        console.error('Failed to fetch tag suggestions:', error);
      }
    };
    
    fetchSuggestions();
  }, []);

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
        setShowSuggestions(false);
        setInputValue('');
      }
    };
    
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Get color for tag based on name
  const getTagColor = (tagName: string) => {
    const lowerTag = tagName.toLowerCase();
    if (SPECIAL_TAG_COLORS[lowerTag]) {
      return SPECIAL_TAG_COLORS[lowerTag];
    }
    // Use hash of tag name to consistently assign a color
    const hash = tagName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return TAG_COLORS[hash % TAG_COLORS.length];
  };

  const handleAddTag = async (tagName: string) => {
    const trimmed = tagName.trim().toUpperCase();
    if (!trimmed || tags.map(t => t.toUpperCase()).includes(trimmed)) {
      setInputValue('');
      setShowSuggestions(false);
      return;
    }
    
    setSavingTag(trimmed);
    try {
      await onAddTag(trimmed);
      setInputValue('');
      setIsExpanded(false);
      setShowSuggestions(false);
      setSavedTag(trimmed);
      setTimeout(() => setSavedTag(null), 2000);
    } catch (error) {
      console.error('Failed to add tag:', error);
    } finally {
      setSavingTag(null);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    setRemovingTag(tag);
    try {
      await onRemoveTag(tag);
    } catch (error) {
      console.error('Failed to remove tag:', error);
    } finally {
      setRemovingTag(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(inputValue);
    } else if (e.key === 'Escape') {
      setIsExpanded(false);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  // Filter suggestions based on input
  const filteredSuggestions = suggestions.filter(
    (s) =>
      !tags.map(t => t.toLowerCase()).includes(s.name.toLowerCase()) &&
      s.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div ref={containerRef} className={`${className}`}>
      {/* Active Tags */}
      <div className="flex flex-wrap gap-2 items-center">
        {tags.map((tag) => {
          const colors = getTagColor(tag);
          return (
            <button
              key={tag}
              onClick={() => canEdit && handleRemoveTag(tag)}
              disabled={removingTag === tag || !canEdit}
              className={`
                group/tag relative px-3 py-2 text-sm font-bold rounded-lg border-2 transition-all duration-300
                ${colors.bg} ${colors.text} ${colors.border}
                ${canEdit ? 'hover:scale-105 cursor-pointer' : 'cursor-default'}
                ${removingTag === tag ? 'opacity-50' : ''}
                ${savedTag === tag ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-white dark:ring-offset-gray-900' : ''}
              `}
            >
              <span className="flex items-center gap-1.5">
                {tag}
                {canEdit && (
                  <X className="w-3.5 h-3.5 opacity-0 group-hover/tag:opacity-100 transition-opacity duration-200" />
                )}
              </span>
              {removingTag === tag && (
                <Loader2 className="absolute -top-1 -right-1 w-4 h-4 animate-spin" />
              )}
              {savedTag === tag && (
                <div className="absolute -top-1 -right-1 bg-emerald-500 rounded-full p-0.5 animate-bounce">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          );
        })}
        
        {/* Add Tag Button - only shows on hover or when expanded */}
        {canEdit && (showAddButton || isExpanded) && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`
              px-3 py-2 text-sm font-medium rounded-lg border-2 border-dashed transition-all duration-300
              ${isExpanded 
                ? 'border-blue-500 dark:border-cyan-500 bg-blue-500/10 dark:bg-cyan-500/10 text-blue-600 dark:text-cyan-300' 
                : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
              }
            `}
          >
            <span className="flex items-center gap-1">
              <Plus className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-45' : ''}`} />
              Add Tag
            </span>
          </button>
        )}
      </div>

      {/* Expandable Available Tags Panel - like Pet page */}
      {isExpanded && (
        <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 animate-in slide-in-from-top-2 duration-200">
          {/* Available Tags Grid */}
          {filteredSuggestions.length > 0 && (
            <>
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Available Tags</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {filteredSuggestions.map((suggestion) => {
                  const colors = getTagColor(suggestion.name);
                  return (
                    <button
                      key={suggestion.name}
                      onClick={() => handleAddTag(suggestion.name)}
                      disabled={savingTag === suggestion.name.toUpperCase()}
                      className={`
                        relative px-3 py-2 text-sm font-bold rounded-lg border-2 transition-all duration-300
                        ${colors.bg} ${colors.text} ${colors.border}
                        hover:scale-105 cursor-pointer
                        ${savingTag === suggestion.name.toUpperCase() ? 'opacity-50' : ''}
                      `}
                    >
                      <span className="flex items-center gap-1.5">
                        <Plus className="w-3.5 h-3.5" />
                        {suggestion.name.toUpperCase()}
                      </span>
                      {savingTag === suggestion.name.toUpperCase() && (
                        <Loader2 className="absolute -top-1 -right-1 w-4 h-4 text-cyan-400 animate-spin" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          
          {/* Custom Tag Input */}
          <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">
            {filteredSuggestions.length > 0 ? 'Or Create Custom Tag' : 'Create New Tag'}
          </p>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              placeholder="Type tag name and press Enter..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 uppercase"
              disabled={savingTag !== null}
            />
            
            {inputValue && !filteredSuggestions.find(s => s.name.toLowerCase() === inputValue.toLowerCase()) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1">
                <button
                  onClick={() => handleAddTag(inputValue)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4 text-gray-400" />
                  Create "{inputValue.toUpperCase()}"
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientTagsInput;
